import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { JoinUsButton, PartnerLogo } from '../atomic';

/**
 * PartnerMarquee - Molecule component for scrolling partner logos with CTAs
 *
 * A marquee component that displays partner logos in a continuous scrolling
 * animation with strategically placed call-to-action buttons. Implements
 * a data-driven approach for easy partner management.
 *
 * @param {Object} props - Component props
 * @param {Array} props.partners - Array of partner objects with logo information
 * @param {Object} [props.config] - Marquee configuration options
 * @param {Function} [props.onCtaClick] - Handler for CTA button clicks
 * @returns {JSX.Element} Rendered marquee with partner logos and CTAs
 *
 * @example
 * <PartnerMarquee
 *   partners={partnersData}
 *   config={marqueeConfig}
 *   onCtaClick={(event) => console.log('CTA clicked')}
 * />
 */
function PartnerMarquee({ partners, config = {}, onCtaClick }) {
  const scrollContainerRef = useRef(null);
  const isHoveredRef = useRef(false);
  const scrollPosRef = useRef(0);

  // Default configuration
  const defaultConfig = {
    displayPattern: [
      { type: 'partners', count: 2 },
      { type: 'cta' },
      { type: 'partners', count: 2 },
      { type: 'cta' },
      { type: 'partners', count: 2 },
      { type: 'cta' },
    ],
    animationDuration: 40,
    pauseOnHover: true,
    gap: 12,
    cta: {
      text: 'Join Us',
      route: '/lets-collaborate',
    },
  };

  // Merge provided config with defaults
  const mergedConfig = {
    ...defaultConfig,
    ...config,
    cta: {
      ...defaultConfig.cta,
      ...config.cta,
    },
  };

  /**
   * Generate marquee content based on pattern configuration
   * @returns {JSX.Element[]} Array of elements to display
   */
  const generateMarqueeContent = () => {
    const elements = [];
    let partnerIndex = 0;
    let elementKey = 0;

    mergedConfig.displayPattern.forEach((item) => {
      if (item.type === 'partners') {
        // Add specified number of partner logos
        const count = item.count || 1;
        for (let i = 0; i < count; i++) {
          if (partnerIndex < partners.length) {
            const partner = partners[partnerIndex];
            elements.push(
              <PartnerLogo
                key={`partner-${elementKey}-${partner.id}`}
                src={partner.logo}
                alt={partner.alt || partner.name}
                size={partner.size}
                loading={partnerIndex < 3 ? 'eager' : 'lazy'}
              />
            );
            partnerIndex++;
            elementKey++;
          }
        }
      } else if (item.type === 'cta') {
        // Add CTA button
        elements.push(
          <JoinUsButton
            key={`cta-${elementKey}`}
            text={mergedConfig.cta.text}
            route={mergedConfig.cta.route}
            onClick={onCtaClick}
          />
        );
        elementKey++;
      }
    });

    // If we didn't use all partners, add the remaining ones at the end
    while (partnerIndex < partners.length) {
      const partner = partners[partnerIndex];
      elements.push(
        <PartnerLogo
          key={`partner-${elementKey}-${partner.id}`}
          src={partner.logo}
          alt={partner.alt || partner.name}
          size={partner.size}
          loading="lazy"
        />
      );
      partnerIndex++;
      elementKey++;
    }

    return elements;
  };

  // Generate content once for both sets (original and duplicate)
  const marqueeContent = generateMarqueeContent();

  /**
   * Continuous auto-scroll with seamless looping in BOTH directions.
   *
   * The track renders the same content twice. Both the timed auto-scroll and
   * manual wheel scrolling drive `scrollLeft` and wrap it at one-copy width, so
   * scrolling (or animating) past the second copy seamlessly reveals the first
   * again. Using scrollLeft for the auto-scroll too (instead of a CSS transform)
   * means the manual wheel position and the auto position share one source of
   * truth and can never dead-end into the blank space after the last copy.
   */
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return undefined;
    }

    // Width of a single content copy (the track holds two identical copies).
    const oneCopyWidth = () => container.scrollWidth / 2;

    // Keep the scroll position within [0, oneCopy); wrapping by exactly one
    // copy width is invisible because the two copies are identical.
    const applyWrap = () => {
      const oneCopy = oneCopyWidth();
      if (oneCopy <= 0) {
        return;
      }
      if (scrollPosRef.current >= oneCopy) {
        scrollPosRef.current -= oneCopy;
      } else if (scrollPosRef.current < 0) {
        scrollPosRef.current += oneCopy;
      }
    };

    let rafId;
    let lastTs = null;
    const tick = (ts) => {
      if (lastTs === null) {
        lastTs = ts;
      }
      const deltaSeconds = (ts - lastTs) / 1000;
      lastTs = ts;

      const paused = mergedConfig.pauseOnHover && isHoveredRef.current;
      if (!paused) {
        // One full copy should scroll past in `animationDuration` seconds,
        // matching the original animation speed.
        const pxPerSecond = oneCopyWidth() / mergedConfig.animationDuration;
        scrollPosRef.current += pxPerSecond * deltaSeconds;
        applyWrap();
        container.scrollLeft = scrollPosRef.current;
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    // Manual horizontal scrolling via the wheel, wrapped the same way.
    const handleWheel = (e) => {
      e.preventDefault();
      scrollPosRef.current += e.deltaY;
      applyWrap();
      container.scrollLeft = scrollPosRef.current;
    };
    container.addEventListener('wheel', handleWheel, { passive: false });

    const handleEnter = () => {
      isHoveredRef.current = true;
    };
    const handleLeave = () => {
      isHoveredRef.current = false;
    };
    container.addEventListener('mouseenter', handleEnter);
    container.addEventListener('mouseleave', handleLeave);

    return () => {
      cancelAnimationFrame(rafId);
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('mouseenter', handleEnter);
      container.removeEventListener('mouseleave', handleLeave);
    };
  }, [mergedConfig.animationDuration, mergedConfig.pauseOnHover]);

  return (
    <Box
      ref={scrollContainerRef}
      sx={{
        mb: 4,
        py: 3,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '12px',
        border:
          '1px solid color-mix(in srgb, var(--color-primary) 20%, transparent)',
        overflow: 'auto',
        position: 'relative',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        // Hide scrollbar but keep functionality
        scrollbarWidth: 'none', // Firefox
        '&::-webkit-scrollbar': {
          display: 'none', // Chrome, Safari
        },
        // Scroll position is driven imperatively (rAF + wheel); keep it instant
        // so each frame's update isn't smoothed/animated.
        scrollBehavior: 'auto',
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          width: 'max-content',
        }}
      >
        {/* First set of content */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: mergedConfig.gap,
            paddingRight: mergedConfig.gap,
            whiteSpace: 'nowrap',
          }}
        >
          {marqueeContent}
        </Box>

        {/* Duplicate set for seamless loop */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: mergedConfig.gap,
            paddingRight: mergedConfig.gap,
            whiteSpace: 'nowrap',
          }}
        >
          {marqueeContent.map((element, index) => ({
            ...element,
            key: `${element.key}-duplicate-${index}`,
          }))}
        </Box>
      </Box>
    </Box>
  );
}

PartnerMarquee.propTypes = {
  /** Array of partner objects */
  partners: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      logo: PropTypes.string.isRequired,
      alt: PropTypes.string,
      size: PropTypes.shape({
        height: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
          PropTypes.object,
        ]),
      }),
      url: PropTypes.string,
      featured: PropTypes.bool,
    })
  ).isRequired,
  /** Marquee configuration */
  config: PropTypes.shape({
    displayPattern: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(['partners', 'cta']).isRequired,
        count: PropTypes.number,
      })
    ),
    animationDuration: PropTypes.number,
    pauseOnHover: PropTypes.bool,
    gap: PropTypes.number,
    cta: PropTypes.shape({
      text: PropTypes.string,
      route: PropTypes.string,
    }),
  }),
  /** CTA click handler */
  onCtaClick: PropTypes.func,
};

export default PartnerMarquee;
